import { WorkoutAlternateGroup } from '@strength/domain-workout';
import { sortByOrder } from '@strength/utils';

/**
 * Extra `exerciseSearch` inputs sent only by the alternate-exercise picker.
 * Both are omitted (undefined) for the primary picker and the swap picker —
 * leaving today's behaviour.
 */
export type AlternateExerciseSearchParams = {
  /** Prescribed exercise the results are ranked against (tag overlap). */
  baseExerciseId?: string;
  /** Group members to omit from results (prescribed exercise + alternates). */
  excludeExerciseIds?: string[];
};

/**
 * Derive the alternate-picker search inputs from the alternate group the caller
 * already holds. Returns an empty object (nothing sent) when there is no
 * alternate group, so callers can safely spread the result into the
 * `exerciseSearch` query input regardless of context.
 *
 * - `baseExerciseId` = the prescribed exercise = the first member of the group
 *   once ordered by `sortOrder` (sortOrder 0).
 * - `excludeExerciseIds` = every group member's `libraryExerciseId` (the
 *   prescribed exercise plus any already-added alternates).
 */
export function getAlternateExerciseSearchParams(
  alternateGroup: WorkoutAlternateGroup | null | undefined,
): AlternateExerciseSearchParams {
  if (!alternateGroup) {
    return {};
  }

  const baseExerciseId = sortByOrder(alternateGroup.exercises)[0]?.exercise
    .libraryExerciseId;
  if (!baseExerciseId) {
    return {};
  }

  return {
    baseExerciseId,
    excludeExerciseIds: alternateGroup.exercises.map(
      (member) => member.exercise.libraryExerciseId,
    ),
  };
}

/**
 * Split an ordered list of tag names into the ones shown inline and the ones
 * collapsed behind the `+N` overflow chip. Pure so the overflow behaviour can
 * be unit-tested without a real layout (width measurement lives in the
 * component). `maxVisible` is clamped to `[0, tags.length]`.
 */
export function splitTagsForDisplay(
  tags: string[],
  maxVisible: number,
): { visible: string[]; hidden: string[] } {
  const clamped = Math.max(0, Math.min(maxVisible, tags.length));
  if (clamped >= tags.length) {
    return { visible: tags, hidden: [] };
  }
  return { visible: tags.slice(0, clamped), hidden: tags.slice(clamped) };
}
